import React, { useMemo } from "react";
import ReactHtmlParser from "react-html-parser";
import { getFormattedUnit } from "config";
import {
  buildRfqTimelineEvents,
  buildRfqTimelineSummary,
  formatAmount,
  formatDateTimeParts,
  formatTimelineChangeValue,
  getRfqTimelineHeroDetails,
  getTimelineToneClasses,
} from "./rfqTimelineUtils";

const formatQuantityUnit = (quantity, unit) => {
  const formattedQuantity =
    quantity !== undefined && quantity !== null && quantity !== ""
      ? formatTimelineChangeValue(quantity, "quantity")
      : "";
  return [formattedQuantity, getFormattedUnit(unit)].filter(Boolean).join(" ") || "--";
};

const RfqDetailedTimeline = ({ rfqData }) => {
  const summaryCards = useMemo(
    () => buildRfqTimelineSummary(rfqData),
    [rfqData],
  );
  const timelineEvents = useMemo(
    () => buildRfqTimelineEvents(rfqData),
    [rfqData],
  );
  const heroDetails = useMemo(
    () => getRfqTimelineHeroDetails(rfqData),
    [rfqData],
  );

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-[#F1DDD2] bg-[linear-gradient(135deg,#FFF8F2_0%,#FFFFFF_58%,#F5F7FF_100%)] shadow-[0_12px_36px_rgba(31,30,41,0.07)]">
        <div className="border-b border-[#F1DDD2] px-4 py-3 lg:px-5">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#E25D19]">
                  RFQ Timeline
                </p>
                <span className="hidden h-4 w-px bg-[#E8D5C9] sm:block" />
                <h1 className="truncate text-lg font-semibold text-[#1F2937]">
                  {rfqData?.name || "RFQ timeline"}
                </h1>
              </div>
              <p className="mt-1 max-w-3xl truncate text-[11px] text-[#6B7280]">
                Detailed journey for RFQ #{rfqData?.rfqId || "--"} covering
                creation, vendor invites, quotation receipt, negotiation,
                approvals, and closing actions.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-5 gap-y-2 rounded-xl border border-[#F3E5D8] bg-white/80 px-3 py-2 sm:flex sm:flex-wrap sm:items-center sm:gap-x-7 xl:min-w-[560px] xl:flex-nowrap">
              {heroDetails.map((item) => (
                <div
                  key={item.label}
                  className={`min-w-0 ${
                    item.label === "Status"
                      ? "sm:flex-none"
                      : "sm:min-w-[110px] sm:flex-auto"
                  }`}
                >
                  <p className="text-[9px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                    {item.label}
                  </p>
                  <p
                    className="truncate text-xs font-semibold text-[#374151]"
                    title={item.value}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 px-4 py-2.5 sm:grid-cols-3 xl:grid-cols-5 lg:px-5">
          {summaryCards.map((card) => (
            <div
              key={card.key}
              className="min-w-0 rounded-xl border border-[#EDE6DC] bg-white/90 px-3 py-2"
            >
              <p className="truncate text-[9px] font-medium uppercase tracking-wide text-[#9CA3AF]">
                {card.label}
              </p>
              <div className="mt-0.5 flex min-w-0 items-baseline gap-2">
                <p className="shrink-0 text-lg font-semibold leading-6 text-[#1F2937]">
                  {card.value}
                </p>
                <p className="truncate text-[10px] text-[#6B7280]">
                  {card.helper}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E9E5DE] bg-white shadow-[0_12px_36px_rgba(31,30,41,0.05)]">
        <div className="flex flex-col border-b border-[#F1EEE8] px-5 py-3 sm:flex-row sm:items-baseline sm:gap-3 lg:px-6">
          <h2 className="text-sm font-semibold text-[#1F2937]">
            Full Journey
          </h2>
          <p className="text-[11px] text-[#6B7280]">
            Chronological view of all tracked RFQ activities.
          </p>
        </div>

        {timelineEvents.length > 0 ? (
          <div className="px-5 py-3 lg:px-6">
            <div className="relative pl-6 sm:pl-0">
              <div className="absolute bottom-0 left-[9px] top-0 w-px bg-[linear-gradient(180deg,#FDBA74_0%,#CBD5E1_22%,#C4B5FD_62%,#FCA5A5_100%)] sm:left-[181px]" />

              <div className="space-y-3">
                {timelineEvents.map((event) => {
                  const toneClasses = getTimelineToneClasses(event.tone);
                  const timestamp = formatDateTimeParts(event.timestamp);
                  const itemSectionNames = [
                    ...new Set(
                      event.itemChanges
                        .map((itemChange) => itemChange?.sectionName)
                        .filter(Boolean),
                    ),
                  ];
                  const rateSectionNames = [
                    ...new Set(
                      event.rateChanges
                        .map((change) => change?.sectionName)
                        .filter(Boolean),
                    ),
                  ];

                  return (
                    <div
                      key={event.id}
                      className="relative grid gap-3 sm:grid-cols-[170px_24px_minmax(0,1fr)] sm:gap-0"
                    >
                      <div className="text-[11px] leading-4 text-[#6B7280] sm:pt-1">
                        <p className="font-semibold text-[#374151]">
                          <span className="block whitespace-nowrap">
                            {timestamp.date}{timestamp.time ? "," : ""}
                          </span>
                          {timestamp.time && (
                            <span className="mt-0.5 block">{timestamp.time}</span>
                          )}
                        </p>
                      </div>

                      <div className="relative hidden sm:block">
                        <div
                          className={`absolute left-1/2 top-2 h-3 w-3 -translate-x-1/2 rounded-full ring-4 ${toneClasses.dot} ${toneClasses.ring}`}
                        />
                      </div>

                      <div className="relative sm:ml-3">
                        <div
                          className={`absolute -left-[21px] top-2 h-3 w-3 rounded-full ring-4 ${toneClasses.dot} ${toneClasses.ring} sm:hidden`}
                        />

                        <div
                          className={`rounded-xl border px-3 py-3 shadow-sm ${toneClasses.card}`}
                        >
                          {event.tag && (
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${toneClasses.badge}`}
                            >
                              {event.tag}
                            </span>
                          )}

                          <h3 className="mt-1.5 text-sm font-semibold text-[#1F2937]">
                            {event.title}
                          </h3>

                          {event.subtitle && (
                            <p className="mt-0.5 text-xs leading-5 text-[#4B5563]">
                              {event.subtitle}
                            </p>
                          )}

                          {event.vendorNames.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-y-1 text-[11px] font-medium leading-4 text-[#374151]">
                              {event.vendorNames.map((vendorName, index) => (
                                <React.Fragment
                                  key={`${event.id}-vendor-${vendorName}-${index}`}
                                >
                                  {index > 0 && (
                                    <span className="mx-2 text-[#94A3B8]">|</span>
                                  )}
                                  <span>{vendorName}</span>
                                </React.Fragment>
                              ))}
                            </div>
                          )}

                          {event.fieldChanges.length > 0 && (
                            <div className="mt-2 max-w-full overflow-x-auto rounded-xl border border-[#CBD5E1] bg-white/75 md:inline-block md:min-w-[50%]">
                              <div className="min-w-[560px]">
                                <div className="grid grid-cols-[150px_minmax(180px,1fr)_minmax(180px,1fr)] gap-3 border-b border-[#CBD5E1] bg-[#F1F5F9] px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[#475569]">
                                  <span>Field</span>
                                  <span>Previous value</span>
                                  <span>Updated value</span>
                                </div>

                                {event.fieldChanges.map((change, index) => (
                                  <div
                                    key={`${event.id}-field-${change.field}-${index}`}
                                    className="grid grid-cols-[150px_minmax(180px,1fr)_minmax(180px,1fr)] gap-3 border-b border-[#E2E8F0] px-3 py-2 text-[11px] leading-4 text-[#4B5563] last:border-b-0"
                                  >
                                    <span className="font-semibold text-[#374151]">
                                      {change.label || change.field}
                                    </span>
                                    <span className="break-words">
                                      {ReactHtmlParser(
                                        String(
                                          formatTimelineChangeValue(
                                            change.previousValue,
                                            change.field,
                                          ),
                                        ),
                                      )}
                                    </span>
                                    <span className="break-words font-semibold text-[#334155]">
                                      {ReactHtmlParser(
                                        String(
                                          formatTimelineChangeValue(
                                            change.updatedValue,
                                            change.field,
                                          ),
                                        ),
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {event.itemChanges.length > 0 && (
                            <div className="mt-2 min-w-0 max-w-full">
                              {itemSectionNames.length > 0 && (
                                <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1.5 text-[10px] text-[#475569]">
                                  <span className="font-semibold uppercase tracking-wide">
                                    Section:
                                  </span>
                                  {itemSectionNames.map((sectionName) => (
                                    <span
                                      key={`${event.id}-section-${sectionName}`}
                                      className="max-w-full break-words rounded-full bg-[#E2E8F0] px-2 py-0.5 font-medium [overflow-wrap:anywhere]"
                                    >
                                      {sectionName}
                                    </span>
                                  ))}
                                </div>
                              )}

                              <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-[#CBD5E1] bg-white/75">
                                <div className="grid min-w-0 grid-cols-[72px_78px_minmax(0,1fr)_minmax(0,1.5fr)] gap-2 border-b border-[#CBD5E1] bg-[#F1F5F9] px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-[#475569] sm:grid-cols-[90px_110px_minmax(0,1fr)_minmax(0,2fr)] sm:gap-3 sm:px-3">
                                  <span>Change</span>
                                  <span>Row</span>
                                  <span>Item name</span>
                                  <span>Details</span>
                                </div>

                                {event.itemChanges.map((itemChange, index) => (
                                  <div
                                    key={`${event.id}-item-${itemChange.itemType}-${itemChange.rowIndex}-${index}`}
                                    className="grid min-w-0 grid-cols-[72px_78px_minmax(0,1fr)_minmax(0,1.5fr)] gap-2 border-b border-[#E2E8F0] px-2 py-2 text-[11px] leading-4 text-[#4B5563] last:border-b-0 sm:grid-cols-[90px_110px_minmax(0,1fr)_minmax(0,2fr)] sm:gap-3 sm:px-3"
                                  >
                                    <span
                                      className={`h-fit w-fit self-start rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                        itemChange.action === "added"
                                          ? "bg-[#DCFCE7] text-[#15803D]"
                                          : itemChange.action === "deleted"
                                            ? "bg-[#FEE2E2] text-[#B91C1C]"
                                            : "bg-[#DBEAFE] text-[#1D4ED8]"
                                      }`}
                                    >
                                      {itemChange.action}
                                    </span>
                                    <span className="font-medium text-[#374151]">
                                      {itemChange.itemType} {itemChange.rowIndex}
                                    </span>
                                    <span className="min-w-0 break-words font-semibold text-[#374151] [overflow-wrap:anywhere]">
                                      {itemChange.name || "--"}
                                    </span>
                                    <div className="min-w-0 space-y-1 break-words [overflow-wrap:anywhere]">
                                      {itemChange.changes.length > 0 ? (
                                        itemChange.changes.map((change, changeIndex) => (
                                          <div
                                            key={`${event.id}-item-change-${index}-${change.field}-${changeIndex}`}
                                          >
                                            <span className="font-semibold">
                                              {change.label}:{" "}
                                            </span>
                                            <span>
                                              {formatTimelineChangeValue(
                                                change.previousValue,
                                                change.field,
                                              )}
                                            </span>
                                            <span className="mx-1 text-[#94A3B8]">→</span>
                                            <span className="font-semibold text-[#334155]">
                                              {formatTimelineChangeValue(
                                                change.updatedValue,
                                                change.field,
                                              )}
                                            </span>
                                          </div>
                                        ))
                                      ) : (
                                        <span>
                                          {itemChange.action === "added"
                                            ? "Added to RFQ"
                                            : "Deleted from RFQ"}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {event.rateChanges.length > 0 && (
                            <div className="mt-2 min-w-0 max-w-full">
                              {rateSectionNames.length > 0 && (
                                <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1.5 text-[10px] text-[#6D28D9]">
                                  <span className="font-semibold uppercase tracking-wide">
                                    Section:
                                  </span>
                                  {rateSectionNames.map((sectionName) => (
                                    <span
                                      key={`${event.id}-rate-section-${sectionName}`}
                                      className="max-w-full break-words rounded-full bg-[#EDE9FE] px-2 py-0.5 font-medium [overflow-wrap:anywhere]"
                                    >
                                      {sectionName}
                                    </span>
                                  ))}
                                </div>
                              )}

                              <div className="w-full min-w-0 max-w-full overflow-hidden rounded-xl border border-[#DDD6FE] bg-white/75">
                                <div className="grid min-w-0 grid-cols-4 gap-1 border-b border-[#CBD5E1] bg-[#F1F5F9] px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-[#475569] sm:grid-cols-[minmax(0,1fr)_90px_110px_110px_120px] sm:gap-3 sm:px-3">
                                  <span className="col-span-4 sm:col-span-1">{event.rateItemLabel} name</span>
                                  <span className="text-right">Qty / Unit</span>
                                  <span className="text-right">Current rate</span>
                                  <span className="text-right">Previous Rate</span>
                                  <span className="text-right">Difference</span>
                                </div>

                                {event.rateChanges.map((change, index) => (
                                  <div
                                    key={`${event.id}-${change.key}-${index}`}
                                    className="grid min-w-0 grid-cols-4 gap-1 border-b border-[#EDE9FE] px-2 py-2 text-[11px] leading-4 text-[#4B5563] last:border-b-0 sm:grid-cols-[minmax(0,1fr)_90px_110px_110px_120px] sm:gap-3 sm:px-3"
                                  >
                                    <div className="col-span-4 min-w-0 break-words font-medium text-[11px] leading-4 text-[#374151] [overflow-wrap:anywhere] [&_*]:break-words [&_*]:text-[11px] [&_*]:leading-4 sm:col-span-1">
                                      {ReactHtmlParser(change.name || "")}
                                    </div>
                                    <span className="min-w-0 break-words text-right [overflow-wrap:anywhere]">
                                      {formatQuantityUnit(change.quantity, change.unit)}
                                    </span>
                                    <span className="text-right font-semibold text-[#6D28D9]">
                                      {formatAmount(change.updatedRate)}
                                    </span>
                                    <span className="text-right">
                                      {Number.isFinite(change.previousRate)
                                        ? formatAmount(change.previousRate)
                                        : "--"}
                                    </span>
                                    <span className={`text-right font-semibold ${
                                      change.difference > 0
                                        ? "text-red-600"
                                        : change.difference < 0
                                          ? "text-green-700"
                                          : "text-[#475569]"
                                    }`}>
                                      {Number.isFinite(change.difference) ? (
                                        <>
                                          {/* {change.difference > 0 && <span aria-hidden="true">{"\u2191 "}</span>}
                                          {change.difference < 0 && <span aria-hidden="true">{"\u2193 "}</span>} */}
                                          {formatAmount(Math.abs(change.difference))}
                                        </>
                                      ) : "--"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {event.meta.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {event.meta.map((metaItem) => {
                                const isSavings = metaItem.startsWith("Savings:");
                                const isIncrease = metaItem.startsWith("Increase:");

                                return (
                                  <span
                                    key={`${event.id}-${metaItem}`}
                                    className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                                      isSavings
                                        ? "border-green-200 bg-green-100 text-green-700"
                                        : isIncrease
                                          ? "border-red-200 bg-red-100 text-red-700"
                                          : "border-white/80 bg-white/70 text-[#5B6473]"
                                    }`}
                                  >
                                    {/* {isSavings && <span aria-hidden="true">{"\u2193 "}</span>}
                                    {isIncrease && <span aria-hidden="true">{"\u2191 "}</span>} */}
                                    {metaItem}
                                  </span>
                                );
                              })}
                            </div>
                          )}

                          {event.grnDetails.length > 0 && (
                            <div className="mt-2 w-full max-w-full overflow-x-auto rounded-xl border border-[#CBD5E1] bg-white/75">
                              <table className="w-full min-w-[520px] border-collapse text-left text-[11px] text-[#4B5563]">
                                <thead className="bg-[#F1F5F9] text-[10px] font-semibold uppercase tracking-wide text-[#475569]">
                                  <tr>
                                    <th className="border-b border-[#CBD5E1] px-3 py-2">GRN Number</th>
                                    <th className="border-b border-[#CBD5E1] px-3 py-2">GRN Date</th>
                                    <th className="border-b border-[#CBD5E1] px-3 py-2">Comments</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {event.grnDetails.map((grn, index) => {
                                    const grnDate = formatDateTimeParts(grn?.grnDate);

                                    return (
                                      <tr key={`${event.id}-grn-${grn?.grnNumber || index}`}>
                                        <td className="border-b border-[#E2E8F0] px-3 py-2 font-semibold text-[#374151]">
                                          {grn?.grnNumber || "--"}
                                        </td>
                                        <td className="border-b border-[#E2E8F0] px-3 py-2">
                                          {grnDate.time
                                            ? `${grnDate.date}, ${grnDate.time}`
                                            : grnDate.date}
                                        </td>
                                        <td className="border-b border-[#E2E8F0] px-3 py-2">
                                          {grn?.grnComments
                                            ? ReactHtmlParser(String(grn.grnComments))
                                            : "--"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-5 py-10 text-center lg:px-6">
            <p className="text-sm font-semibold text-[#1F2937]">
              No tracked timeline events yet
            </p>
            <p className="mt-1 text-xs text-[#6B7280]">
              This RFQ does not have enough activity data to build a detailed
              timeline right now.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RfqDetailedTimeline;
